/** Truncates long axis/legend labels for chart readability. */
export function truncateChartLabel(text, maxLen = 16) {
  const s = String(text ?? "");
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}…`;
}

/**
 * Keeps the top N items by value and groups the rest into a single "Others" bucket.
 * Returns metadata so the UI can show a grouping hint.
 */
export function groupTopChartItems(items, options = {}) {
  const {
    maxItems = 8,
    valueKey = "value",
    nameKey = "name",
    othersLabel = "Outros",
    sortDescending = true,
  } = options;

  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (list.length <= maxItems) {
    return {
      items: list,
      grouped: false,
      totalCount: list.length,
      hiddenCount: 0,
    };
  }

  const sorted = [...list].sort((a, b) => {
    const av = Number(a[valueKey]) || 0;
    const bv = Number(b[valueKey]) || 0;
    return sortDescending ? bv - av : av - bv;
  });

  const top = sorted.slice(0, maxItems - 1);
  const rest = sorted.slice(maxItems - 1);
  const othersValue = rest.reduce((sum, row) => sum + (Number(row[valueKey]) || 0), 0);

  return {
    items: [
      ...top,
      {
        [nameKey]: othersLabel,
        [valueKey]: othersValue,
        _groupedCount: rest.length,
      },
    ],
    grouped: true,
    totalCount: list.length,
    hiddenCount: rest.length,
  };
}

/**
 * Limits line-chart series to the top N keys by total volume; remaining series are summed into "Others".
 */
export function limitTrendSeries(data, options = {}) {
  const {
    maxSeries = 8,
    monthKey = "month",
    othersLabel = "Outros",
  } = options;

  const rows = Array.isArray(data) ? data.filter(Boolean) : [];
  if (!rows.length) {
    return { data: [], keys: [], grouped: false, totalSeries: 0, hiddenSeries: 0 };
  }

  const sample = rows.find((r) => r && typeof r === "object") || {};
  const keys = Object.keys(sample).filter((k) => k !== monthKey);
  if (keys.length <= maxSeries) {
    return {
      data: rows,
      keys,
      grouped: false,
      totalSeries: keys.length,
      hiddenSeries: 0,
    };
  }

  const totals = keys
    .map((key) => ({
      key,
      total: rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0),
    }))
    .sort((a, b) => b.total - a.total);

  const topKeys = totals.slice(0, maxSeries - 1).map((t) => t.key);
  const restKeys = totals.slice(maxSeries - 1).map((t) => t.key);

  const nextData = rows.map((row) => {
    const next = { [monthKey]: row[monthKey] };
    for (const key of topKeys) next[key] = row[key];
    next[othersLabel] = restKeys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0);
    return next;
  });

  return {
    data: nextData,
    keys: [...topKeys, othersLabel],
    grouped: true,
    totalSeries: keys.length,
    hiddenSeries: restKeys.length,
  };
}

/** Dynamic chart height for horizontal ranked bar charts. */
export function rankedBarChartHeight(itemCount, { min = 280, max = 520, rowHeight = 32 } = {}) {
  const n = Math.max(1, Number(itemCount) || 1);
  return Math.min(max, Math.max(min, n * rowHeight));
}
